import {
  requestSession,
  submitEffectivenessReview,
  submitSessionReview,
} from "@/api/coaching"
import { COACHING_STEP, SERVICE_TYPE } from "@/constants/coaching"
import router from "@/router"
import { PAYMENT_METHODS } from "@/store/coaching/coaching.constants"
import { truncateFloatToSignificantDigit } from "@/store/payment/payment.utils"
import { CURRENCY_TYPES } from "@/store/user/inventory/inventory.constants"
import { MIXINS } from "@/utils/mixins"

const state = {
  step: COACHING_STEP.INIT,
  previousStep: null,
  serviceType: SERVICE_TYPE.INSTANT_SESSION,
  serviceQuantity: 1,
  selectedService: null,
  authStep: null,
  selectedRegion: "",
  selectedPlatform: "",
  studentUsername: "",
  studentDiscord: "",
  saveInfo: true,
  loading: false,
  checkoutError: "",
  endingSession: false,
}
const getters = {
  step: state => state.step,
  previousStep: state => state.previousStep,
  serviceType: state => state.serviceType,
  serviceQuantity: state => state.serviceQuantity,
  selectedService: state => state.selectedService,
  authStep: state => state.authStep,
  selectedRegion: state => state.selectedRegion,
  selectedPlatform: state => state.selectedPlatform,
  studentUsername: state => state.studentUsername,
  studentDiscord: state => state.studentDiscord,
  coachGame: (state, getters, rootState) => {
    try {
      const gameKey = router.currentRoute.params["game"]
      const selectedCoach = rootState.Coaching.selectedCoach
      if (gameKey) {
        return selectedCoach.coach.games.find(
          game => game.game.name === gameKey
        )
      }
      return selectedCoach.coach.games[0]
    } catch (error) {
      return {}
    }
  },
  getUserPriceForCoachService: (state, getters, rootState, rootGetters) => (service) => {
    const coachServiceDiscount = rootGetters["User/coachServiceDiscount"] || 0
    const discountPercentage = coachServiceDiscount / 100
    const servicePriceInUsd = service?.price_amount ?? null
    const priceCurrency = service?.price_currency ?? ""
    if (servicePriceInUsd === null || priceCurrency.toUpperCase() !== "USD") {
      // Currently only able to handle USD
      return null
    }

    const adjustedServicePriceInUsd = servicePriceInUsd * (1 - discountPercentage)
    return Math.max(0, truncateFloatToSignificantDigit(adjustedServicePriceInUsd, 2))
  },
  // For now, this is a getter computed from the user's primary credit currency. Move into state when we support manual
  // selection of payment method.
  selectedPaymentMethod: (state, getters, rootState, rootGetters) => {
    switch (rootGetters["User/Inventory/getPrimaryCreditCurrency"]) {
      case CURRENCY_TYPES.PG_POINTS:
        return PAYMENT_METHODS.PG_POINTS
      case CURRENCY_TYPES.PB_CREDIT:
        return PAYMENT_METHODS.PB_CREDIT
      default:
        return null
    }
  },
  subscribedToCoach: (state, getters, rootState) => {
    try {
      const coachId = rootState.Coaching.selectedCoach.coach.id
      const activeCoachSubscriptions = rootState.User.coachSubscription
        ? rootState.User.coachSubscription.active
        : []

      return activeCoachSubscriptions.find(
        subscription =>
          subscription.coach_game.coach.id === coachId &&
          getters.coachGame.game.name === subscription.coach_game.game.name
      )
    } catch (error) {
      return null
    }
  },
  saveInfo: state => state.saveInfo,
  loading: state => state.loading,
  checkoutError: state => state.checkoutError,
  endingSession: state => state.endingSession,
}
const mutations = {
  setStep: (state, step) => {
    // Reset history when navigating back to initial state
    state.previousStep = step === COACHING_STEP.INIT
      ? null
      : state.step
    state.step = step
  },
  setServiceType: (state, serviceType) => (state.serviceType = serviceType),
  setServiceQuantity: (state, serviceQuantity) =>
    (state.serviceQuantity = serviceQuantity),
  setSelectedService: (state, selectedService) =>
    (state.selectedService = selectedService),
  setAuthStep: (state, authStep) => (state.authStep = authStep),
  setSelectedRegion: (state, region) => (state.selectedRegion = region),
  setSelectedPlatform: (state, platform) => (state.selectedPlatform = platform),
  setStudentUsername: (state, username) => (state.studentUsername = username),
  setStudentDiscord: (state, discord) => (state.studentDiscord = discord),
  setSaveInfo: (state, saveInfo) => (state.saveInfo = saveInfo),
  setLoading: (state, loading) => (state.loading = loading),
  setCheckoutError: (state, checkoutError) =>
    (state.checkoutError = checkoutError),
  setEndingSession: (state, endingSession) =>
    (state.endingSession = endingSession),
}
const actions = {
  resetProfile ({ commit }) {
    commit("setStep", COACHING_STEP.INIT)
    commit("setServiceType", SERVICE_TYPE.INSTANT_SESSION)
    commit("setSelectedService", null)
    commit("setServiceQuantity", 1)
    commit("setAuthStep", null)
    commit("setCheckoutError", "")
  },
  async requestSession ({ commit, state, dispatch, rootGetters, getters }) {
    commit("setCheckoutError", "")
    commit("setLoading", true)
    const instaProAccessToken = await dispatch(
      "User/Coaching/loadInstaProAccessToken",
      null,
      {
        root: true,
      }
    )
    const coachId = rootGetters["Coaching/selectedCoach"].id
    const gameKey = router.currentRoute.params["game"]
    const gameId = getters.coachGame.game_id
    const body = {
      coach_id: coachId,
      game: gameKey,
      game_id: gameId,
      type: state.selectedService.session_type.key,
      platform: state.selectedPlatform,
      message: "fake message",
      region: state.selectedRegion,
      total: state.serviceQuantity,
      in_game_username: state.studentUsername,
      communication_type: "discord",
      communication_name: state.studentDiscord,
      payment_method: getters.selectedPaymentMethod,
    }
    try {
      throw new Error("Sessions can no longer be requested.")
      const data = await requestSession(body, instaProAccessToken)
    } catch (e) {
      const errorMessage = e.response?.data?.message
      commit("setCheckoutError", errorMessage)
    }
    commit("setLoading", false)
  },
  async submitSessionReview ({ dispatch, rootGetters }, body) {
    const instaProAccessToken = await dispatch(
      "User/Coaching/loadInstaProAccessToken",
      null,
      {
        root: true,
      }
    )
    const activeRequest = rootGetters["Coaching/activeSessionRequest"]
    if (activeRequest) {
      submitSessionReview(activeRequest.id, body, instaProAccessToken)
    }
    MIXINS.methods.trackAnalyticsEvent("Rate Coach")
  },
  async submitEffectivenessReview ({ dispatch, rootGetters }, body) {
    const instaProAccessToken = await dispatch(
      "User/Coaching/loadInstaProAccessToken",
      null,
      {
        root: true,
      }
    )
    const activeRequest = rootGetters["Coaching/activeSessionRequest"]
    if (activeRequest) {
      submitEffectivenessReview(activeRequest.id, body, instaProAccessToken)
    }
    MIXINS.methods.trackAnalyticsEvent("Rate Coach")
  },
  selectSubscription ({ commit }, subscription) {
    commit("setServiceType", SERVICE_TYPE.SUBSCRIPTION)
    commit("setSelectedService", subscription)
    commit("setStep", COACHING_STEP.SELECTING)
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}



// WEBPACK FOOTER //
// ./src/store/modules/coaching/CoachService.js